import { useMemo, memo } from 'react';
import type { RefObject } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { SDropdownMenu, SText, SConfirmDialog } from '@setvi/shared/components';
import {
  AddBySKUOutlineIcon,
  AddSPOOutlineIcon,
  DeleteIconV2,
  EditIcon,
  FillBagOutlineIcon,
  MoveIcon,
  PlusIcon,
  UnlinkIcon
} from '@setvi/shared/components/sicons';
import {
  QuoteSectionDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
import { useDialog } from '@setvi/shared/providers';
import type { VirtualItem } from '@tanstack/react-virtual';
import { ProductRow } from '../components/product-row';
import { InsertDivider } from '../components/insert-divider';
import { SectionEmptyState } from './components/section-empty-state';
import {
  useQuoteData,
  useQuoteItemsData,
  useQuoteMutations,
  useQuoteStatusFlags
} from '../../../context';
import { AddFromCatalogDialog } from '../../add-from-catalog-dialog';
import { SectionDialog } from '../../add-section-dialog';
import { AddBySku } from '../../add-by-sku';
import { AddSPO } from '../../add-spo';
import { MoveItemsToSectionDialog } from '../../move-items-to-section-dialog';
import { useInnerVirtualizer } from '../hooks/use-inner-virtualizer';
import { useSectionLock } from '../../../pages/details/realtime/use-field-locks';
import {
  HIGHLIGHT_BOX_SHADOW,
  QuoteRowPresenceBadge,
  useQuoteRowPresence
} from '../../../pages/details/realtime/quote-row-presence';

const useStyles = makeStyles(() => ({
  sectionContainer: {
    position: 'relative',
    width: '100%',
    padding: '12px',
    backgroundColor: '#EEF0F2',
    borderRadius: '10px'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: '#FFFFFF',
    padding: '8px 12px',
    borderRadius: '12px'
  },
  productsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
    minHeight: '50px',
    borderRadius: '10px'
  },
  // Each child wrapper has a transform (own stacking context), so a later
  // sibling would paint over the previous child's insert button. Lift the
  // hovered child above its siblings so its insert affordance wins.
  childWrapper: {
    '&:hover': {
      zIndex: 20
    }
  }
}));

interface SectionRowProps {
  section: QuoteSectionDTO;
  virtualRow: VirtualItem;
  scrollContainerRef: RefObject<HTMLDivElement>;
  searchQuery?: string;
  onProductClick?: (product: QuoteProductDTO) => void;
}

/**
 * Section component with products
 * Displays section name, description, and its products
 */
const SECTION_HEADER_HEIGHT = 60;

export const SectionRow = memo(
  ({
    section,
    virtualRow,
    scrollContainerRef,
    searchQuery,
    onProductClick
  }: SectionRowProps) => {
    const classes = useStyles();
    const { openDialog, closeDialog } = useDialog();
    const { quote, currentStatus } = useQuoteData();
    const { items: quoteItems } = useQuoteItemsData();
    const {
      addProducts,
      addFreehand,
      removeSection,
      removeSectionKeepItems,
      updateSection,
      moveItems
    } = useQuoteMutations();
    const { isAddingProducts, isAddingFreehand } = useQuoteStatusFlags();

    const products = section.children ?? [];

    const { virtualItems: innerVirtualItems, totalSize: innerTotalSize } =
      useInnerVirtualizer({
        products,
        scrollContainerRef,
        sectionStartOffset: virtualRow.start,
        sectionHeaderHeight: SECTION_HEADER_HEIGHT,
        enabled: products.length > 0
      });

    const isQuoteEditable =
      !!currentStatus?.isQuoteEditable && !!quote?.acl?.canEditQuote;
    const notEditableTooltip = 'Quote is not editable';

    const sectionId = section.id || section._id;

    // Presence: show who's on THIS section header and broadcast it on hover.
    const { members, rowProps, isHighlighted } = useQuoteRowPresence(sectionId);

    // Collaborative section lock: held while THIS user has any section action
    // open, so other collaborators see the whole section menu disabled. Release
    // runs on every close path (explicit close + backdrop/escape).
    const sectionLock = useSectionLock(sectionId);
    const sectionLockTooltip = sectionLock.lockedByOther
      ? `${sectionLock.holder?.name || 'Another user'} is editing this section`
      : notEditableTooltip;
    const openSectionDialog = (
      component: (handleClose: () => void) => JSX.Element,
      id: string
    ) => {
      if (sectionLock.lockedByOther) return;
      sectionLock.acquire();
      const releaseSection = () => sectionLock.release();
      const handleClose = () => {
        releaseSection();
        closeDialog(id);
      };
      openDialog(component(handleClose), { id, onClose: releaseSection });
    };

    // Get items that can be moved to this section (root-level products not in any section)
    const availableItemsToMove = useMemo(() => {
      if (!quoteItems) return [];

      return quoteItems.filter(item => {
        // Only include products (not sections)
        if (item.type !== 'product') return false;
        // Only include root-level items (no parentID/parentId)
        const product = item as any;
        return !product.parentID && !product.parentId;
      }) as QuoteProductDTO[];
    }, [quoteItems]);

    const handleAddFromCatalog = () => {
      openSectionDialog(
        handleClose => (
          <AddFromCatalogDialog
            onClose={handleClose}
            parentId={sectionId}
            quote={quote as any}
            addProducts={addProducts}
          />
        ),
        `add-from-catalog-section-${sectionId}`
      );
    };

    const handleAddBySKU = () => {
      openSectionDialog(
        handleClose => (
          <AddBySku
            onClose={handleClose}
            parentId={sectionId}
            addProducts={addProducts}
            isAddingProducts={isAddingProducts}
            selectedCustomer={quote?.account as any}
            segmentId={quote?.segment?._id}
          />
        ),
        `add-by-sku-section-${sectionId}`
      );
    };

    const handleAddSPO = () => {
      openSectionDialog(
        handleClose => (
          <AddSPO
            onClose={handleClose}
            onSubmit={async spoData => {
              await addFreehand({ ...spoData, sectionId });
              handleClose();
            }}
            isAddingProducts={isAddingFreehand}
          />
        ),
        `add-spo-section-${sectionId}`
      );
    };

    const handleRemoveSectionKeepItems = () => {
      openSectionDialog(
        handleClose => (
          <SConfirmDialog
            type="delete"
            title="Remove Section Only"
            description="This will remove the section header. The items (if there's any) will remain in your quote, but they will no longer be grouped under this section."
            confirmText="Remove Section"
            closeText="Cancel"
            onClose={handleClose}
            onConfirm={() => {
              removeSectionKeepItems(sectionId);
              handleClose();
            }}
          />
        ),
        `remove-section-keep-items-${sectionId}`
      );
    };

    const handleRemoveSection = () => {
      const hasItems = section.children && section.children.length > 0;
      const title = hasItems
        ? 'Remove Section & Items Inside'
        : 'Remove Section';
      const description = hasItems
        ? 'This will permanently remove the section and all items it contains from the Quote.'
        : 'This will permanently remove the section from the Quote.';
      const confirmText = hasItems
        ? 'Remove Section & Items'
        : 'Remove Section';

      openSectionDialog(
        handleClose => (
          <SConfirmDialog
            type="delete"
            title={title}
            description={description}
            additionalMessage={
              hasItems ? 'This action CANNOT be undone.' : undefined
            }
            confirmText={confirmText}
            closeText="Cancel"
            onClose={handleClose}
            onConfirm={() => {
              removeSection(sectionId);
              handleClose();
            }}
          />
        ),
        `remove-section-${sectionId}`
      );
    };

    const handleEditSection = () => {
      openSectionDialog(
        handleClose => (
          <SectionDialog
            section={section}
            updateSection={updateSection}
            onClose={handleClose}
          />
        ),
        `edit-section-${sectionId}`
      );
    };

    const handleMoveExistingItems = () => {
      openSectionDialog(
        handleClose => (
          <MoveItemsToSectionDialog
            section={section}
            availableItems={availableItemsToMove}
            onClose={handleClose}
            onConfirm={itemIds => {
              // Move all selected items to this section at once
              moveItems(itemIds, sectionId);
              handleClose();
            }}
          />
        ),
        `move-items-to-section-${sectionId}`
      );
    };

    const menuItems = [
      {
        icon: <EditIcon fontSize="small" />,
        text: 'Edit Section Title & Note',
        color: '#333D47',
        onClick: handleEditSection
      },
      {
        icon: <PlusIcon fontSize="small" />,
        text: 'Add Items to Section',
        color: '#333D47',
        submenu: [
          {
            icon: <FillBagOutlineIcon fontSize="small" />,
            text: 'Add From Catalog',
            color: '#333D47',
            onClick: handleAddFromCatalog
          },
          {
            icon: <AddBySKUOutlineIcon fontSize="small" />,
            text: 'Add by SKU',
            color: '#333D47',
            onClick: handleAddBySKU
          },
          {
            icon: <AddSPOOutlineIcon fontSize="small" />,
            text: 'Add SPO',
            color: '#333D47',
            onClick: handleAddSPO
          }
        ]
      },
      {
        icon: <MoveIcon fontSize="small" />,
        text: 'Move Existing Items to Section',
        color: '#333D47',
        onClick: handleMoveExistingItems,
        disabled: availableItemsToMove.length === 0
      },
      // Only show "Remove Section (Keep Items)" when section has items
      ...(section.children && section.children.length > 0
        ? [
            {
              icon: <UnlinkIcon fontSize="small" />,
              text: 'Remove Section (Keep Items)',
              color: '#333D47',
              onClick: handleRemoveSectionKeepItems
            }
          ]
        : []),
      {
        icon: <DeleteIconV2 fontSize="small" />,
        // Show "Remove Section" when no items, "Remove Section & Items" when has items
        text:
          section.children && section.children.length > 0
            ? 'Remove Section & Items'
            : 'Remove Section',
        color: 'danger',
        onClick: handleRemoveSection
      }
    ];

    return (
      <div className={classes.sectionContainer} {...rowProps}>
        <QuoteRowPresenceBadge members={members} />
        <Box
          className={classes.sectionHeader}
          style={{
            transition: 'box-shadow 0.4s ease',
            boxShadow: isHighlighted ? HIGHLIGHT_BOX_SHADOW : undefined
          }}>
          <Box>
            <SText title={section.name} weight="semibold" />
            {section.note && (
              <SText title={section.note} size="sm" maxLines={3} />
            )}
          </Box>
          <SDropdownMenu
            newDesign
            menuItems={isQuoteEditable ? menuItems : []}
            disabled={!isQuoteEditable || sectionLock.lockedByOther}
            disabledTooltip={sectionLockTooltip}
          />
        </Box>

        <div
          className={classes.productsContainer}
          style={{
            height: products.length > 0 ? innerTotalSize : undefined,
            position: products.length > 0 ? 'relative' : undefined
          }}>
          {products.length > 0 ? (
            innerVirtualItems.map(innerRow => {
              const product = products[innerRow.index];
              const productId = product.id || product._id;

              return (
                <div
                  key={productId}
                  data-index={innerRow.index}
                  className={classes.childWrapper}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${innerRow.start}px)`
                  }}>
                  <ProductRow
                    isSectionChild
                    product={product}
                    searchQuery={searchQuery}
                    onProductClick={onProductClick}
                    scrollContainerRef={scrollContainerRef}
                    productStartOffset={
                      virtualRow.start + SECTION_HEADER_HEIGHT + innerRow.start
                    }
                  />
                  {/* Insert a product between this child and the next, within
                      the section (disabled during search). */}
                  {!searchQuery && <InsertDivider afterItemId={productId} />}
                </div>
              );
            })
          ) : (
            <SectionEmptyState
              items={quoteItems || []}
              onAddFromCatalog={handleAddFromCatalog}
              onAddBySKU={handleAddBySKU}
              onAddSPO={handleAddSPO}
              onMoveExistingItems={handleMoveExistingItems}
              disabled={!isQuoteEditable || sectionLock.lockedByOther}
            />
          )}
        </div>
      </div>
    );
  }
);
